import _styled from "styled-components";
import type { SanityPageBuilderDeepDiveGallerySection, SanityPageBuilderDeepDiveGalleryStep, SanityPageBuilderSection } from "@ouster/shared";
import { Asset, type Props as AssetProps } from "~/features/craft/asset";
import { type CraftAsset, toCraftInlineVideoAsset } from "~/features/sanity/media";
import { DeepDiveGalleryBlockView, type DeepDiveGalleryBlockViewProps } from "~/features/blocks/inventory/deep-dive-gallery";
import { type DeepDiveGalleryControl } from "~/features/blocks/inventory/deep-dive-gallery/context";
import { DeepDiveGalleryControlsView } from "~/features/blocks/inventory/deep-dive-gallery/deep-dive-gallery-controls";
import { DeepDiveGalleryControlView } from "~/features/blocks/inventory/deep-dive-gallery/deep-dive-gallery-controls/deep-dive-gallery-control";
import { DeepDiveGalleryStepsView } from "~/features/blocks/inventory/deep-dive-gallery/deep-dive-gallery-steps";
import { DeepDiveGalleryStepView } from "~/features/blocks/inventory/deep-dive-gallery/deep-dive-gallery-steps/deep-dive-gallery-step";
import { toCraftAsset } from "~/features/sanity/media";
import { SanityRichText } from "~/features/sanity/rich-text";
import { VideoControls } from "~/features/ui/video/video-controls";
type Props = {
  section: SanityPageBuilderSection & {
    loaded?: boolean;
    sectionContent: SanityPageBuilderDeepDiveGallerySection;
  };
};
type DeepDiveGalleryViewStep = DeepDiveGalleryControl & {
  _key?: SanityPageBuilderDeepDiveGalleryStep["_key"];
  hasBlur?: boolean;
  headline?: string;
  image?: CraftAsset;
  isIntro?: boolean;
  text?: React.ReactNode;
  video?: CraftAsset;
};
export function SanityDeepDiveGallerySection({
  section
}: Props) {
  const {
    sectionContent,
    sectionSettings
  } = section;
  const video = toCraftInlineVideoAsset(sectionContent.video);
  const steps = sectionContent.steps?.map<DeepDiveGalleryViewStep>(step => ({
    _key: step._key,
    hasBlur: step.hasBlur,
    headline: step.headline,
    image: toCraftAsset(step.image),
    isIntro: step.isIntro,
    text: step.text?.length ? <SanityRichText value={step.text} /> : undefined,
    video: toCraftInlineVideoAsset(step.video),
    videoEnd: step.videoEnd,
    videoStart: step.videoStart
  })) ?? [];
  const anchor: DeepDiveGalleryBlockViewProps["anchor"] = {
    id: sectionSettings?.sectionId?.current || sectionSettings?.anchorName || section._key,
    name: sectionSettings?.anchorName
  };
  return <DeepDiveGalleryBlockView anchor={anchor} controls={steps} hasOverlay={sectionContent.hasOverlay} hasTopDivider={sectionSettings?.hasTopDivider ?? true} sectionProps={{
    "data-sanity-section-key": section._key,
    "data-sanity-section-type": section._type
  }} theme={sectionSettings?.theme || "white"} videoSource={video?.src ? {
    mimeType: video.mimeType,
    src: video.src,
    type: video.type
  } : undefined}>
      <DeepDiveGalleryStepsView getStepKey={(step, index) => step._key || index} renderStep={(step, index, isActive) => <DeepDiveGalleryStepView hasBlur={step.hasBlur} headline={step.headline} index={index} isActive={isActive} isIntro={step.isIntro} renderMedia={({
      setAutoPlayStatus,
      setRenderGalleryCursor,
      videoRef
    }) => step.video ? <Asset asset={step.video as AssetProps["asset"]} imageProps={{
      fit: "cover"
    }} inlineVideoProps={{
      videoRef: videoRef,
      fit: "cover",
      autoPlay: false,
      loop: true,
      poster: step.image?.src,
      controls: <_StyledVideoControls videoRef={videoRef} onClick={() => setAutoPlayStatus("paused")} onMouseEnter={() => setRenderGalleryCursor(false)} onMouseLeave={() => setRenderGalleryCursor(true)} />
    }} /> : step.image ? <Asset asset={step.image as AssetProps["asset"]} imageProps={{
      fit: "cover"
    }} /> : null} text={step.text} />} steps={steps} />
      <DeepDiveGalleryControlsView id={section._key || ""} controls={steps} getControlKey={(step, index) => step._key || index} renderControl={(step, index) => <DeepDiveGalleryControlView image={<Asset asset={step.image as AssetProps["asset"]} imageProps={{
      width: 100,
      fit: "cover"
    }} />} index={index} />} />
    </DeepDiveGalleryBlockView>;
}
var _StyledVideoControls = /*#__PURE__*/_styled(VideoControls).withConfig({
  displayName: "deep-dive-gallery___StyledVideoControls",
  componentId: "sc-1ks7fqa-0"
})({
  "position": "absolute",
  "right": "3.6rem",
  "top": "3.2rem",
  "zIndex": "20"
});